/**
 * This module lists constants for the IEP extraction editor.
 */

// Acronyms that should stay upper-cased when humanizing snake_case keys.
const ACRONYMS = new Set(['iep', 'fape', 'lre', 'esy', 'bip', 'fba', 'sai', 'plop', 'id'])

// Friendly titles for the well-known top-level sections. Anything not listed falls back to the
// generic humanizer below.
const SECTION_TITLES = {
  metadata: 'Document Info',
  goals: 'Goals',
  accommodationsAndModifications: 'Accommodations & Modifications',
  accommodations: 'Accommodations',
  modifications: 'Modifications',
  presentLevels: 'Present Levels of Performance',
  services: 'Services',
  recommendations: 'Recommendations',
  areasOfNeedAnalysis: 'Areas of Need — Analysis',
  areasOfNeedRecommendations: 'Areas of Need — Recommendations',
  areasOfNeedSummary: 'Areas of Need — Summary',
  fapeDisabilityLre: 'FAPE, Disability & LRE',
  assessments: 'Assessments',
  signatures: 'Signatures',
  behaviorInterventionPlans: 'Behavior Intervention Plans',
  parentConcerns: 'Parent Concerns',
  contacts: 'Contacts',
  notifications: 'Notifications',
  summaries: 'Summaries',
  progressReports: 'Progress Reports',
  priorities: 'Priorities',
  outcomes: 'Outcomes',
  supports: 'Supports'
}

// Singular labels used on the “Add…” button for each array section.
const ADD_LABELS = {
  goals: 'Goal',
  secondaryGoals: 'Sub-goal',
  accommodationsAndModifications: 'Accommodation',
  accommodations: 'Accommodation',
  modifications: 'Modification',
  presentLevels: 'Present Level',
  services: 'Service',
  recommendations: 'Recommendation',
  areasOfNeedAnalysis: 'Area of Need',
  areasOfNeedRecommendations: 'Recommendation',
  assessments: 'Assessment',
  signatures: 'Signature',
  behaviorInterventionPlans: 'Plan',
  parentConcerns: 'Concern',
  contacts: 'Contact',
  evidenceQuotes: 'Quote',
  targetBehaviors: 'Behavior',
  hypothesizedFunctions: 'Function',
  replacementBehaviors: 'Behavior',
  preventativeStrategies: 'Strategy',
  interventionStrategies: 'Strategy',
  priorities: 'Priority',
  outcomes: 'Outcome',
  supports: 'Support'
}

// Top-level keys that are machine plumbing rather than IEP content. Still editable, but tucked
// into a collapsed “Advanced” group so non-engineers aren’t confronted with them first.
const TECHNICAL_SECTIONS = new Set([
  '_sectionClassifications',
  'preVerificationServices',
  'goalToPresentLevelAdjacency',
  'presentLevelToAccommodationModificationAdjacency'
])

// Required top-level fields per section-item type — the single source of truth for both marking
// inputs as required in ExtractionFieldEditor and gating/explaining the "Add" flow in
// IepSectionItemEditDrawer. A section listing more than one key (e.g. plops) accepts either name
// for the same logical field, since the extraction pipeline can emit either.
const REQUIRED_FIELDS = {
  goals: new Set(['goalText']),
  services: new Set(['serviceName']),
  accommodations: new Set(['text']),
  modifications: new Set(['text']),
  accommodationsAndModifications: new Set(['text']),
  plops: new Set(['text', 'plopText']),
  presentLevels: new Set(['text', 'plopText']),
  // A recommendation isn't its own drawer sectionKey — it's nested inside a goal/service/
  // accommodation/modification's own `recommendations` array — so isFieldRequired falls back to
  // this set for a recommendation's own fields regardless of the outer section (see ./utilities).
  recommendations: new Set(['concernText'])
}

// IPP extraction vocabularies. Priorities, outcomes, and goals share one shape; services and
// supports share another. The goals and services section keys collide with the IEP tables above,
// so IPP lookups resolve through these tables instead — see resolveAllowedFields, fillDefaultKeys,
// and isRequiredFieldsSatisfied in ./utilities. Field names follow the modeled IPP items (see
// model.ipp, PogInfo, ServiceSupportInfo); adjust here if the extraction payload names differ.
const IPP_POG_ALLOWED_FIELDS = new Set([
  'domain',
  'statementText',
  'goalText',
  'measurementApproach',
  'responsibleParty',
  'targetDate',
  'pageNumber',
  'sourceQuote'
])

const IPP_SERVICE_SUPPORT_ALLOWED_FIELDS = new Set([
  'serviceType',
  'details',
  'authorizationNumber',
  'frequency',
  'fundingSource',
  'hours',
  'provider',
  'startDate',
  'endDate',
  'pageNumber',
  'sourceQuote'
])

const IPP_ALLOWED_SECTION_FIELDS = {
  priorities: IPP_POG_ALLOWED_FIELDS,
  outcomes: IPP_POG_ALLOWED_FIELDS,
  goals: IPP_POG_ALLOWED_FIELDS,
  services: IPP_SERVICE_SUPPORT_ALLOWED_FIELDS,
  supports: IPP_SERVICE_SUPPORT_ALLOWED_FIELDS
}

// As with REQUIRED_FIELDS, a section listing more than one key is satisfied by any one of them.
const IPP_REQUIRED_FIELDS = {
  priorities: new Set(['statementText']),
  outcomes: new Set(['statementText']),
  goals: new Set(['statementText', 'goalText']),
  services: new Set(['serviceType', 'details']),
  supports: new Set(['serviceType', 'details'])
}

const IPP_REQUIRED_FIELD_LABELS = {
  priorities: 'Statement',
  outcomes: 'Statement',
  goals: 'Goal',
  services: 'Service type',
  supports: 'Support type'
}

// Friendly noun for a section's primary required field, used in "X is required." messaging.
const REQUIRED_FIELD_LABELS = {
  goals: 'Goal',
  services: 'Service name',
  accommodations: 'Text',
  modifications: 'Text',
  plops: 'Text'
}

// Keys whose string values are narrative prose and deserve a multi-line text box.
const LONG_TEXT_KEY =
  /(text|description|summary|rationale|justification|note|narrative|comment|quote|concern|gap|strategy|strategies|response|outline|clarification|definition|detailing|specification|approach|notification|example)/i

const DATE_KEY = /(^date$|_date$|date_)/i

// Accepts a full ISO date, a year-month, a bare year, or empty — the extraction emits all of these.
// Also accepts full ISO/UTC timestamps (with T or space, offsets, timezone markers) emitted by the backend.
const PARTIAL_DATE = /^\d{4}(-\d{2}(-\d{2})?)?([T ]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/
const FULL_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

// The delay (in milliseconds) before scrolling to the selected node to allow layout/expansion to finish.
const SCROLL_TIMEOUT = 80

const ALLOWED_SECTION_FIELDS = {
  goals: new Set([
    'goalText',
    'baselineText',
    'listedPerformanceArea',
    'performanceArea',
    'measurement',
    'responsiblePerson',
    'targetDate',
    'goalNumber',
    'number',
    'goalStructureAssessment',
    'recommendations',
    'secondaryGoals'
  ]),
  services: new Set([
    'serviceName',
    'serviceType',
    'location',
    'field',
    'provider',
    'startDate',
    'endDate',
    'durationMinutes',
    'duration',
    'frequency',
    'esy',
    'percentInGeneralEd',
    'percentOutsideGeneralEd',
    'comments'
  ]),
  accommodations: new Set(['text', 'startDate', 'endDate', 'location', 'frequency', 'duration', 'relatedToTests']),
  modifications: new Set(['text', 'startDate', 'endDate', 'location', 'frequency', 'duration', 'relatedToTests']),
  plops: new Set(['title', 'category', 'text', 'plopText', 'listedPerformanceArea', 'date'])
}

// Bookkeeping key splitCombinedSections stamps onto each item with its original index in the
// combined array, so mergeCombinedSections can restore the original document order on save
// regardless of which split key an item ended up under. Never persisted — stripped by
// mergeCombinedSections before the merged array is handed back, and hidden from the form the same
// way `id`/`uniqueIdentifier` are (see ALWAYS_EXCLUDED_KEYS below).
const SPLIT_ORDER_KEY = '__splitOrder'

// Bookkeeping keys hidden from the form wherever they appear, no matter how deeply nested —
// unlike EXCLUDED_KEYS_BY_SECTION below, this isn't scoped to top-level section items, since
// nested list items (e.g. a goal's secondaryGoals entries) carry the same client-only ids but
// fall outside that scoping (see isSectionItem in filterChildKeys).
const ALWAYS_EXCLUDED_KEYS = new Set(['id', 'uniqueIdentifier', SPLIT_ORDER_KEY])

// Sections that are one combined array in the extraction document, but that IepExtractionEditorDialog
// splits into separate top-level keys (by a discriminant field on each item) right after loading —
// see splitCombinedSections/mergeCombinedSections in ./utilities. Only that dialog does this;
// IepSectionItemEditDrawer keeps working against the combined key as-is. Adding an entry here is all
// it takes to split another combined section the same way.
//
// `defaultKey` is where an item lands when its `field` value doesn't match any `parts` entry (a
// missing/null/unrecognized `type`, e.g. from an older or hand-edited extraction) — without it,
// such items would silently vanish from the document on the next save. This mirrors the lenient
// "anything not explicitly a modification is an accommodation" rule the rest of this module already
// assumes (see resolveAllowedFields and fillDefaultKeys in ./utilities).
const SPLIT_SECTIONS = {
  accommodationsAndModifications: {
    field: 'type',
    defaultKey: 'accommodations',
    parts: [
      { key: 'accommodations', value: 'accommodation' },
      { key: 'modifications', value: 'modification' }
    ]
  }
}

const EXCLUDED_KEYS_BY_SECTION = {
  accommodations: ['id', 'createdAt', 'updatedAt'],
  priorities: ['id', 'createdAt', 'updatedAt'],
  outcomes: ['id', 'createdAt', 'updatedAt'],
  supports: ['id', 'createdAt', 'updatedAt'],
  accommodationsAndModifications: ['id', 'createdAt', 'updatedAt'],
  goals: ['id', 'createdAt', 'updatedAt'],
  modifications: ['id', 'createdAt', 'updatedAt'],
  plops: ['id', 'createdAt', 'updatedAt'],
  presentLevels: ['id', 'createdAt', 'updatedAt'],
  services: ['id', 'createdAt', 'updatedAt']
}

export {
  ACRONYMS,
  IPP_ALLOWED_SECTION_FIELDS,
  IPP_REQUIRED_FIELDS,
  IPP_REQUIRED_FIELD_LABELS,
  SECTION_TITLES,
  ADD_LABELS,
  TECHNICAL_SECTIONS,
  REQUIRED_FIELDS,
  REQUIRED_FIELD_LABELS,
  LONG_TEXT_KEY,
  DATE_KEY,
  PARTIAL_DATE,
  FULL_ISO_DATE,
  ALLOWED_SECTION_FIELDS,
  EXCLUDED_KEYS_BY_SECTION,
  ALWAYS_EXCLUDED_KEYS,
  SCROLL_TIMEOUT,
  SPLIT_SECTIONS,
  SPLIT_ORDER_KEY
}
